import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Headphones, Phone, X, Users, Clock, MessageCircle, Volume2, VolumeX
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { notificationSound } from '../lib/notificationSound';

export const LiveChatNotification = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [agentStatus, setAgentStatus] = useState(null);
  const [queueCount, setQueueCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [latestChat, setLatestChat] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(notificationSound.isEnabled());
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const heartbeatRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const lastQueueCountRef = useRef(0);

  // Only show for agents
  const isAgent = ['itil_staff', 'admin', 'super_admin'].includes(user?.role);

  // Fetch agent status and queue stats
  const fetchStatus = useCallback(async () => {
    if (!isAgent) return;
    
    try {
      const [status, stats] = await Promise.all([
        api.getAgentStatus(),
        api.getChatQueueStats()
      ]);
      setAgentStatus(status);
      
      // Check if queue count increased - play sound for new chats
      const newQueueCount = stats.total_in_queue;
      if (newQueueCount > lastQueueCountRef.current && lastQueueCountRef.current >= 0 && status?.is_available_for_chat) {
        console.log('[LiveChatNotification] Queue increased via polling, playing notification');
        // New chat detected via polling - play sound
        (async () => {
          try {
            await notificationSound.playChime();
          } catch (e) {
            console.error('[LiveChatNotification] Failed to play polling chime:', e);
          }
        })();
      }
      lastQueueCountRef.current = newQueueCount;
      setQueueCount(newQueueCount);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  }, [isAgent]);

  useEffect(() => {
    fetchStatus();
    // Poll more frequently (every 15 seconds) for better reliability
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // WebSocket for real-time notifications
  useEffect(() => {
    if (!user?.id || !isAgent) return;

    const connectWebSocket = () => {
      try {
        const wsUrl = api.getLiveChatWebSocketUrl(user.id);
        console.log('[LiveChatNotification] Connecting WebSocket:', wsUrl);
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          console.log('[LiveChatNotification] WebSocket connected');
          setWsConnected(true);
          heartbeatRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
            }
          }, 30000);
          
          // Pre-initialize audio context when WebSocket connects
          notificationSound.init().then(() => {
            console.log('[LiveChatNotification] Audio pre-initialized on WS connect');
          }).catch(e => console.log('[LiveChatNotification] Audio init deferred:', e));
        };

        wsRef.current.onmessage = (event) => {
          const data = JSON.parse(event.data);
          console.log('[LiveChatNotification] WebSocket message:', data.type);
          
          if (data.type === 'new_chat_request') {
            console.log('[LiveChatNotification] New chat request received:', data);
            setQueueCount(prev => prev + 1);
            lastQueueCountRef.current = queueCount + 1;
            setLatestChat(data);
            setShowPopup(true);
            
            // Auto-hide after 10 seconds
            setTimeout(() => setShowPopup(false), 10000);
            
            // Play notification sound - ensure audio is ready first
            (async () => {
              try {
                console.log('[LiveChatNotification] Attempting to play chime for new chat...');
                // Force resume in case tab was backgrounded
                await notificationSound.forceResume();
                const played = await notificationSound.playChime();
                console.log('[LiveChatNotification] Chime result:', played);
              } catch (e) {
                console.error('[LiveChatNotification] Failed to play chime:', e);
              }
            })();
            
            // Show browser notification if permitted
            if (Notification.permission === 'granted') {
              new Notification('New Live Chat Request', {
                body: `${data.user_name} is waiting for assistance`,
                icon: '/favicon.ico',
                tag: 'live-chat-' + Date.now(),
                requireInteraction: true
              });
            }
          } else if (data.type === 'chat_transferred_to_you') {
            // Play urgent alert for transfers
            (async () => {
              try {
                await notificationSound.forceResume();
                await notificationSound.playUrgentAlert();
              } catch (e) {
                console.error('[LiveChatNotification] Failed to play urgent alert:', e);
              }
            })();
            setShowPopup(true);
            setLatestChat(data);
            toast.info(`Chat transferred to you from ${data.from_agent}`);
          } else if (data.type === 'chat_closed' || data.type === 'agent_joined') {
            setQueueCount(prev => {
              const newCount = Math.max(0, prev - 1);
              lastQueueCountRef.current = newCount;
              return newCount;
            });
          }
        };

        wsRef.current.onclose = () => {
          console.log('[LiveChatNotification] WebSocket disconnected');
          setWsConnected(false);
          clearInterval(heartbeatRef.current);
          // Reconnect after 5 seconds
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };
        
        wsRef.current.onerror = (error) => {
          console.error('[LiveChatNotification] WebSocket error:', error);
          setWsConnected(false);
        };
      } catch (error) {
        console.error('WebSocket connection error:', error);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      }
    };

    // Initialize audio context and request notification permission on first click
    const handleFirstInteraction = async () => {
      console.log('[LiveChatNotification] First interaction - initializing audio');
      await notificationSound.init();
      if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
      document.removeEventListener('click', handleFirstInteraction);
    };
    document.addEventListener('click', handleFirstInteraction);
    
    // Also listen for visibility change to resume audio when tab becomes active
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('[LiveChatNotification] Tab visible - resuming audio context');
        await notificationSound.forceResume();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    connectWebSocket();

    return () => {
      clearInterval(heartbeatRef.current);
      clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, isAgent, queueCount]);

  // Toggle availability
  const toggleAvailability = async () => {
    try {
      const newStatus = !agentStatus?.is_available_for_chat;
      await api.updateAgentStatus({ 
        is_available_for_chat: newStatus,
        max_concurrent_chats: 3
      });
      setAgentStatus(prev => ({ ...prev, is_available_for_chat: newStatus }));
      toast.success(newStatus ? 'You are now available for chats' : 'You are now offline');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Toggle sound
  const toggleSound = async () => {
    // Initialize audio context on user click (required by browsers)
    await notificationSound.init();
    
    const newState = await notificationSound.toggle();
    setSoundEnabled(newState);
    
    if (newState) {
      toast.success('Chat notification sound enabled');
    } else {
      toast.info('Chat notification sound disabled');
    }
  };

  // Handle pickup
  const handlePickup = async () => {
    if (latestChat?.session_id) {
      try {
        await api.pickupChat(latestChat.session_id);
        toast.success('Chat picked up!');
        setShowPopup(false);
        navigate('/live-chat');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to pickup chat');
      }
    }
  };

  if (!isAgent) return null;

  // Test sound function for debugging
  const testSound = async () => {
    console.log('[LiveChatNotification] Testing sound...');
    await notificationSound.init();
    await notificationSound.playChime();
  };

  return (
    <>
      {/* Status Indicator in Header */}
      <div className="flex items-center gap-1">
        {/* Sound Toggle - double click to test */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSound}
          onDoubleClick={testSound}
          className="h-8 w-8 p-0"
          title={soundEnabled ? 'Sound On - Click to toggle, Double-click to test' : 'Sound Off - Click to enable'}
          data-testid="header-sound-toggle"
        >
          {soundEnabled ? (
            <Volume2 className="h-4 w-4 text-green-500" />
          ) : (
            <VolumeX className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
        
        {/* Availability Toggle */}
        <Button
          variant={agentStatus?.is_available_for_chat ? 'default' : 'outline'}
          size="sm"
          onClick={toggleAvailability}
          className={`relative ${agentStatus?.is_available_for_chat ? 'bg-green-600 hover:bg-green-700' : ''}`}
          data-testid="header-chat-status"
        >
          <Headphones className="h-4 w-4" />
          {queueCount > 0 && agentStatus?.is_available_for_chat && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs animate-pulse"
            >
              {queueCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Incoming Chat Popup */}
      {showPopup && latestChat && agentStatus?.is_available_for_chat && (
        <div 
          className="fixed top-20 right-4 z-50 animate-in slide-in-from-right-5 duration-300"
          data-testid="chat-notification-popup"
        >
          <div className="bg-card border rounded-lg shadow-lg p-4 w-80">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm">New Chat Request</p>
                  <p className="text-xs text-muted-foreground">{latestChat.user_name}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0"
                onClick={() => setShowPopup(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {latestChat.initial_message && (
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                "{latestChat.initial_message}"
              </p>
            )}
            
            <div className="flex items-center gap-2 mb-3 text-xs text-muted-foreground">
              {latestChat.ticket_number && (
                <Badge variant="outline">{latestChat.ticket_number}</Badge>
              )}
              <div className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                <span>{queueCount} in queue</span>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={handlePickup}
              >
                <Phone className="h-4 w-4 mr-1" />
                Pickup
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setShowPopup(false);
                  navigate('/live-chat');
                }}
              >
                View Queue
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
